import axios from 'axios';

import { getBackendUrl } from '../utils/apiConfig';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: getBackendUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Court Management API
export const courtAPI = {
  // Courts
  getAllCourts: (activeOnly = true) => 
    apiClient.get(`/api/courts/?active_only=${activeOnly}`),

  getPublicPadelCourts: () =>
    apiClient.get('/api/courts/public/padel'),
  
  getCourt: (courtId) => 
    apiClient.get(`/api/courts/${courtId}`),
  
  createCourt: (courtData) => 
    apiClient.post('/api/courts/', courtData),
  
  updateCourt: (courtId, updates) => 
    apiClient.put(`/api/courts/${courtId}`, updates),
  
  deleteCourt: (courtId) => 
    apiClient.delete(`/api/courts/${courtId}`),

  purgeInactiveCourts: () =>
    apiClient.delete('/api/courts/purge-inactive'),

  resetPadelInventory: () =>
    apiClient.post('/api/courts/admin/reset-padel-inventory'),

  // Time Slots
  createTimeSlot: (courtId, slotData) => 
    apiClient.post(`/api/courts/${courtId}/timeslots`, slotData),
  
  bulkCreateTimeSlots: (courtId, bulkData) => 
    apiClient.post(`/api/courts/${courtId}/timeslots/bulk`, bulkData),
  
  generateWeeklySchedule: (courtId, startDate) => 
    apiClient.post(`/api/courts/${courtId}/schedule/generate?start_date=${startDate}`),
  
  getCourtAvailability: (courtId, date) => 
    apiClient.get(`/api/courts/${courtId}/availability?target_date=${date}`),
  
  getCourtSchedule: (courtId, date) => 
    apiClient.get(`/api/courts/${courtId}/schedule?target_date=${date}`),

  // Pricing
  createPricingRule: (ruleData) => 
    apiClient.post('/api/courts/pricing-rules', ruleData),
  
  getPricingRules: (activeOnly = true) => 
    apiClient.get(`/api/courts/pricing-rules?active_only=${activeOnly}`),
  
  calculatePrice: (courtType, duration, date, startTime) => 
    apiClient.get(`/api/courts/pricing/calculate?court_type=${courtType}&duration=${duration}&target_date=${date}&start_time=${startTime}`),

  // Utility
  getCourtTypes: () => 
    apiClient.get('/api/courts/types/available'),
  
  getDashboardStats: () => 
    apiClient.get('/api/courts/dashboard/stats'),

  // Calendar & Time Slot Management
  getCourtCalendar: (courtId, startDate, endDate) => 
    apiClient.get(`/api/courts/calendar/${courtId}?start_date=${startDate}&end_date=${endDate}`),

  blockTimeSlot: (timeslotId, blockReason) => 
    apiClient.patch(`/api/courts/timeslots/${timeslotId}/block?block_reason=${encodeURIComponent(blockReason)}`),

  unblockTimeSlot: (timeslotId) => 
    apiClient.patch(`/api/courts/timeslots/${timeslotId}/unblock`),
};

// Helper functions
export const courtHelpers = {
  formatCourtType: (type) => {
    const types = {
      'padel_1v1': 'Padel 1V1',
      'padel_2v2': 'Padel 2V2', 
      'basket_1v1': 'Basket 1V1',
      'badminton': 'Badminton'
    };
    return types[type] || type;
  },

  formatLocation: (location) => {
    const locations = {
      'indoor': 'Indoor',
      'outdoor': 'Outdoor'
    };
    return locations[location] || location || '—';
  },

  formatDuration: (duration) => {
    const durations = {
      '1h': '1 heure',
      '1h30': '1h30',
      '2h': '2 heures'
    };
    return durations[duration] || duration;
  },

  formatTime: (timeString) => {
    if (!timeString) return '';
    // Handle both 'HH:MM:SS' and 'HH:MM' formats
    const parts = timeString.split(':');
    return `${parts[0]}:${parts[1]}`;
  },

  formatPrice: (price) => {
    return `${price}€`;
  },

  getStatusColor: (isAvailable, isBlocked) => {
    if (isBlocked) return 'red';
    if (isAvailable) return 'green';
    return 'orange'; // reserved
  },

  getStatusLabel: (isAvailable, isBlocked) => {
    if (isBlocked) return 'Bloqué';
    if (isAvailable) return 'Disponible';
    return 'Réservé';
  }
};

export default apiClient;